import queryString from 'query-string'

import { getAuthenticityToken } from 'Shared/helpers'
import FetcherFactory from 'Shared/FetcherFactory'

import { JobStatusEnum } from '../constants'

/**
 * DISCLAIMER: This is a very specific Fetcher.
 * It *could* be used as a baseline for other pollers
 * The most important thing to keep in mind is that at the time of writing this:
 *
 * << THIS "POLLING" IS NOT REALLY A TRADITIONAL POLLING. >>
 * << WE FETCH THE ENTIRE JOB STATE EACH REQUEST RATHER THAN SMALL UPDATES >>
 *
 * With the above information in mind, we can pretty much use a slightly modified
 * fetcher factory.
 *
 * This poller will have the following state structure:
 * data: {
 *   [ParametrizedJob1ID]: { Serialized Job1 data }
 *   [ParametrizedJob2ID]: { Serialized Job2 data }
 *   [ParametrizedJob3ID]: { Serialized Job3 data }
 * }
 *
 * This will hold ALL possible jobs. Later, we can look in the reducer for specific
 * jobs that we want to re-poll or prevent polling for them.
 */

export const reducerName = 'CLONE_LEAGUES_SESSIONS_JOBS_POLLER'
export const CLONE_LEAGUES_SESSION_ID = 'clone_leagues_session_id'

const transformJobStatus = (jobStatus) => {
  if (jobStatus === 'waiting') {
    return JobStatusEnum.SCHEDULED
  }

  if (jobStatus === 'running') {
    return JobStatusEnum.IN_PROGRESS
  }

  return jobStatus
}

const transformJobsData = (jobsData) => {
  Object.keys(jobsData.jobs).forEach((jobID) => {
    jobsData.jobs[jobID]['job']['status'] = transformJobStatus(jobsData.jobs[jobID]?.job?.status)
  })
}


const factory = new FetcherFactory(
  reducerName,
  ({params, state, dispatch, factory}) => {
    const serializedParams = queryString.stringify(params)
    const serializedQueryString = new URLSearchParams(serializedParams).toString()

    return fetch(`${state.baseURL}?${serializedQueryString}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': getAuthenticityToken(),
        'Content-Type': 'application/json',
      },
    }).then(response => {
      return response.json().then(data => {
        transformJobsData(data)
        return ({
          data: data,
          status: response.status,
        })
      })
    })
  },
  {
    ignoreParamsCaching: true,
    handleSuccessAction: (state, data) => {
      return {
        ...state,
        loading: false,
        error: null,
        data: { ...state.data, ...data},
        lastFetchedAt: Date.now(),
      }
    },
    handleErrorAction: (state, error) => {
      return {
        ...state,
        loading: false,
        error: error,
        lastFetchedAt: Date.now(),
      }
    },
  },
)

export const getInitialState = ({pollCloneLeaguesSessionURL}) => {
  return {
    data: {},
    baseURL: pollCloneLeaguesSessionURL,
  }
}

export const cloneSessionsJobsPollerReducer = factory.getReducer()
export const pollCloneSessionJobs = factory.getFetchAction()

export const pollCloneSessionJobsByID = (cloneLeagueSessionID) =>
  pollCloneSessionJobs({
    [CLONE_LEAGUES_SESSION_ID]: cloneLeagueSessionID,
  })

export const selectPolledJobsData = state => state[factory.name]?.data

export const selectPolledJobsList = jobsIDs => state => {
  let jobsDataList = []

  if (!jobsIDs?.length) {
    return null
  }

  const jobsData = state[factory.name]?.data?.jobs

  jobsIDs.forEach(jobID => {
    const jobData = jobsData?.[jobID]

    if (!jobData) {
      return
    }

    jobsDataList = [ ...jobsDataList, jobData ]
  })

  return jobsDataList
}

export const selectPolledJobsByStatus = jobsIDs => state => {
  const jobsByStatus = {
    [JobStatusEnum.IN_PROGRESS]: 0,
    [JobStatusEnum.COMPLETED]: 0,
    [JobStatusEnum.SCHEDULED]: 0,
    [JobStatusEnum.ERROR]: 0,
  }

  if (!jobsIDs?.length) {
    return jobsByStatus
  }
  
  const jobsData = state[factory.name]?.data?.jobs

  Object.values(JobStatusEnum).forEach(status => {
    jobsByStatus[status] = {}
  })

  jobsIDs.forEach(jobID => {
    const jobData = jobsData?.[jobID]

    if (!jobData?.job?.status) {
      return
    }

    if (!jobsByStatus[jobData?.job?.status]) {
      return
    }

    jobsByStatus[jobData?.job?.status][jobID] = jobData
  })

  return jobsByStatus
}

export const namedReducer = {[reducerName]: cloneSessionsJobsPollerReducer}
